import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';

// ── Context ───────────────────────────────────────────────
const BreadcrumbContext = createContext(null);

export function BreadcrumbProvider({ children }) {
  const [crumbs, setCrumbs] = useState([]);
  const setBreadcrumbs = useCallback((items) => setCrumbs(items), []);
  return (
    <BreadcrumbContext.Provider value={{ crumbs, setBreadcrumbs }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────
export function useBreadcrumbs(items) {
  const { setBreadcrumbs } = useContext(BreadcrumbContext);
  useEffect(() => {
    if (items !== null && items !== undefined) setBreadcrumbs(items); // ← null überspringen
    return () => setBreadcrumbs([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items)]);
}

// ── UI ────────────────────────────────────────────────────
const linkSx = {
  display: 'flex', alignItems: 'center', gap: 0.5,
  fontSize: '0.8rem', cursor: 'pointer',
  color: 'text.secondary', textDecoration: 'none',
  '&:hover': { color: 'primary.main' },
};

export default function BreadcrumbBar() {
  const { crumbs } = useContext(BreadcrumbContext);
  const navigate = useNavigate();

  if (!crumbs.length) return null;

  return (
    <Box sx={{
      position: 'sticky', top: 0, zIndex: 100,
      mx: -3, my: -3, px: 3, py: 1, mb: 2,
      bgcolor: 'background.paper',
      borderBottom: '1px solid', borderColor: 'divider',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
        aria-label="breadcrumb"
      >
        <Link onClick={() => navigate('/')} sx={linkSx}>
          <HomeOutlinedIcon sx={{ fontSize: 15 }} />
          Start
        </Link>
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return isLast ? (
            <Typography key={i} sx={{ fontSize: '0.8rem', color: 'text.primary', fontWeight: 500 }}>
              {crumb.label}
            </Typography>
          ) : (
            <Link key={i} onClick={() => crumb.path && navigate(crumb.path)} sx={linkSx}>
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}